<template>
    <div v-if="localization" class="locale-switch">
        <transition
            name="locale-accordion"
            @enter="start"
            @after-enter="end"
            @before-leave="start"
            @after-leave="end"
        >
            <div
                class="locale-switch__background"
                v-if="localization.initialized"
                v-show="opened"
            >
                <div class="locale-switch__container">
                    <div
                        class="locale-switch__locale"
                        v-for="(localeInfo, locale) in localization.supportedLocales"
                        :key="locale"
                        :class="{
                            'locale-switch__locale__selected': locale === localization.locale.locale,
                            disabled: loading,
                        }"
                        role="button"
                        tabindex="0"
                        @click="onLocaleChange((typeof locale === 'string' ? locale : ''))"
                        @keydown.space="onLocaleChange((typeof locale === 'string' ? locale : ''))"
                        @keydown.enter="onLocaleChange((typeof locale === 'string' ? locale : ''))"
                    >
                        <OtLocaleFlag class="locale-switch__locale__flag" :locale="localeInfo" align-left />
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import { inject, onMounted, ref } from 'vue';
import type { GlobalizeLocaleInfo, GlobalizeLocaleDateTime } from '@openticket/lib-localization';
import type { Localization } from '../localization';
import OtLocaleFlag from './OtLocaleFlag.vue';

type Props = {
    title?: string,
    subtitle?: string,
}
type Emits = {
    (e: 'opened', value: boolean): void,
    (e: 'change', value: GlobalizeLocaleInfo<string> & GlobalizeLocaleDateTime): void,
    (e: 'error', value: unknown): void,
}
withDefaults(defineProps<Props>(), {
    title: 'common.localization.switch.title',
    subtitle: 'common.localization.switch.subtitle',
});
const emit = defineEmits<Emits>();

const loading = ref(false);
const opened = ref(false);

const localization: Localization | undefined = inject<Localization>('localization');

const isElementWithStyleHeight = (el: Element): el is Element & { style: { height: string } } => !!el
    && typeof el === 'object' && 'style' in el && !!el.style && typeof el.style === 'object'
    && 'height' in el.style && typeof el.style.height === 'string';

const start = (el: Element): void => {
    if (isElementWithStyleHeight(el)) {
        el.style.height = `${el.scrollHeight}px`;
    }
};

const end = (el: Element): void => {
    if (isElementWithStyleHeight(el)) {
        el.style.height = '';
    }
};

if (!localization) {
    throw new Error('Localization not provided.');
}

onMounted(() => {
    emit('opened', opened.value);
});

async function onLocaleChange(locale: string) {
    loading.value = true;
    try {
        if (!localization) {
            emit('error', new Error('Localization not provided.'));

            return;
        }

        await localization.setLocale(locale);
        emit('change', localization.locale);
    } catch (error) {
        emit('error', error);
    } finally {
        loading.value = false;
        close();
    }
}

function toggle(): void {
    opened.value = !opened.value;

    emit('opened', opened.value);
}

function open(): void {
    opened.value = true;

    emit('opened', opened.value);
}

function close(): void {
    opened.value = false;

    emit('opened', opened.value);
}

defineExpose({
    close,
    open,
    toggle,
});
</script>

<style scoped>
.locale-switch {
    width: 100%;
}

.locale-switch, .locale-switch * {
    box-sizing: border-box;
}

.locale-switch__background {
    position: relative;
    margin: 0;
    padding: 0;
    overflow: hidden;

    background: var(--ot-card-background, #ffffff);
    border-radius: var(--ot-card-border-radius, 0.75rem);
    box-shadow: var(--ot-card-shadow, 0px 12px 32px -16px rgba(30, 40, 53, 0.25));
    color: var(--ot-card-color, #000000);
}

.ot-document.is-dark .locale-switch__background {
    box-shadow: none;
    border: 2px solid var(--ot-color-core-accent-secondary, #424D5C);
    background: var(--ot-card-background, #000000);
    color: var(--ot-card-color, #ffffff);
}

.locale-switch__background.locale-accordion-enter-active,
.locale-switch__background.locale-accordion-leave-active {
    will-change: height, opacity;
    transition-property: height, opacity;
    transition-duration: var(--ot-transition-default-duration, 0.15s);
    transition-timing-function: var(--ot-transition-default-timing-function, ease-out);
    overflow: hidden;
}

.locale-switch__background.locale-accordion-enter,
.locale-switch__background.locale-accordion-leave-to {
    height: 0 !important;
    opacity: 0;
}

.locale-switch__container {
    position: relative;
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, .5fr);
    grid-auto-flow: row;
    grid-gap: 1px;

    background: var(--ot-box-accent, rgba(0,0,0,0.03));
}

.ot-document.is-dark .locale-switch__container {
    background: var(--ot-box-accent, rgba(255,255,255,0.1));
}

@media (max-width: 25rem) {
    .locale-switch__container {
        grid-template-columns: repeat(1, 1fr);
    }
}

.locale-switch__locale {
    display: block;
    padding: var(--ot-card-padding, var(--ot-spacing-lg, 1.5rem));
    background: var(--ot-card-background, #ffffff);

    cursor: pointer;
}

.locale-switch__locale__selected, .locale-switch__locale:hover {
    background-color: transparent;
}

.locale-switch__locale.disabled {
    pointer-events: none;
}
</style>
